/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as jose from 'jose';

// UPDATE URL WITH PROD AND QA ENV VARIABLE
const JWKS = jose.createRemoteJWKSet(
  new URL(
    `${process.env.REACT_APP_KEYCLOAK_AUTH_URL}/protocol/openid-connect/certs`,
  ),
);
export class AWSKeycloakService {
  parsedToken: any = null;
  rawToken: any = null;
  private refreshInFlight: Promise<any> | null = null;
  private refreshTokenInFlight: string | null = null;
  isTokenValid = async (token: string): Promise<boolean> => {
    try {
      await jose.jwtVerify(token, JWKS, {
        issuer: `${process.env.REACT_APP_KEYCLOAK_AUTH_URL}`,
        audience: 'bite_client_local',
      });
      return true;
    } catch (error) {
      console.error('Error verifying token:', error);
      return false;
    }
  };
  // Deliberately unguarded: a token we cannot decode must reject the refresh.
  // Swallowing the error left parsedToken/rawToken holding the PREVIOUS
  // credentials while requestTokenRefresh still returned response.data, so
  // updateToken() reported success and the 401 interceptor replayed the request
  // with the expired access token. The caller's catch logs and rethrows.
  private decodeToken = (tokenObject: any) => {
    const decodedToken = jwtDecode(tokenObject.access_token);

    this.parsedToken = decodedToken;
    this.rawToken = tokenObject;
  };
  private requestTokenRefresh = async (token?: string): Promise<any> => {
    const data = new URLSearchParams();
    data.append('client_id', 'bite_client_local');
    data.append('grant_type', 'refresh_token');
    data.append('refresh_token', token || this.rawToken?.refresh_token || '');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_KEYCLOAK_AUTH_URL}/protocol/openid-connect/token`,
        data,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );
      this.decodeToken(response.data);
      return response.data;
    } catch (error) {
      console.error('Login From AWS', error);
      // Callers must be able to distinguish an invalid refresh token from a
      // temporary network failure. Swallowing this error made updateToken()
      // report success while the app continued using an expired access token.
      throw error;
    }
  };

  refreshToken = (token?: string): Promise<any> => {
    const effectiveToken = token ?? this.rawToken?.refresh_token;

    if (!effectiveToken) {
      return Promise.reject(
        new Error('Unable to update token, no refresh token available.'),
      );
    }

    if (this.refreshInFlight) {
      // Only share an exchange when it belongs to this session. A new login
      // carrying a different explicit token must never receive credentials from
      // the session whose refresh happened to start first.
      if (this.refreshTokenInFlight === effectiveToken) {
        return this.refreshInFlight;
      }

      // Serialize a different session behind the current request. The old
      // caller still receives its rejection; this catch only ensures that a
      // failed old session cannot prevent the new session from authenticating.
      return this.refreshInFlight
        .catch(() => undefined)
        .then(() => this.refreshToken(effectiveToken));
    }

    const request = this.requestTokenRefresh(effectiveToken);
    this.refreshInFlight = request;
    this.refreshTokenInFlight = effectiveToken;

    return request.finally(() => {
      // A queued session may have installed a newer request by the time this
      // callback runs. Only the request that owns the fields may clear them.
      if (this.refreshInFlight === request) {
        this.refreshInFlight = null;
        this.refreshTokenInFlight = null;
      }
    });
  };

  isTokenExpired = (minValiditySeconds = 0): boolean => {
    if (typeof this.parsedToken?.exp !== 'number') {
      return true;
    }
    return (
      this.parsedToken.exp * 1000 <= Date.now() + minValiditySeconds * 1000
    );
  };

  getToken = () => this.rawToken?.access_token;

  getUser = () => {
    return {
      id: this.parsedToken?.sub,
      name: this.parsedToken?.name,
      email: this.parsedToken?.email,
      resource_access: this.parsedToken?.resource_access,
      realm_roles: this.parsedToken?.roles,
    };
  };
  // could use admin client here

  getUsername = () => this.parsedToken?.preferred_username;

  // Checks if User has the Realm Role, "bite-admin" (Super Admin User for all BITE groups)
  isBiteAdmin = () => this.parsedToken?.roles.includes('bite-admin');

  isServiceAccount = () => this.parsedToken?.roles.includes('service-account');
  hasRealmRoles = (roles: string[]) => {
    return roles.some((role) => this.parsedToken?.roles.includes(role));
  };
  // Checks if any roles in a roles array are realm roles
  doLogout = (redirectUri?: string) => {
    this.parsedToken = null;
    this.rawToken = null;
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = redirectUri || `${window.location.origin}/`;
  };
}
export {};
